function resetProductFormLoadingButtons(root = document) {
  root.querySelectorAll('.product-form__submit').forEach((btn) => {
    btn.classList.remove('loading');
    if (btn.getAttribute('aria-disabled') === 'true') btn.removeAttribute('aria-disabled');
    btn.querySelector('.loading__spinner')?.classList.add('hidden');
  });
}

if (!window.__productFormLoadingResetBound) {
  window.__productFormLoadingResetBound = true;
  const resetOnReturn = () => resetProductFormLoadingButtons();
  window.addEventListener('pageshow', resetOnReturn);
  window.addEventListener('pagehide', resetOnReturn);
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form) return;

        this._productFormBound = true;
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.submitButton = this.querySelector('[type="submit"]:not([data-checkout-button])');
        this.checkoutButton = this.querySelector('[data-checkout-button]');
        this.submitButtonText = this.submitButton?.querySelector('span');

        if (document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      resetLoadingButtons() {
        resetProductFormLoadingButtons(this);
      }

      getCart() {
        return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      }

      cartReady(cart) {
        return !!(cart && typeof cart.getSectionsToRender === 'function' && typeof cart.renderContents === 'function');
      }

      async ensureCart() {
        let cart = this.getCart();
        if (cart && !this.cartReady(cart)) {
          try {
            await customElements.whenDefined(cart.tagName.toLowerCase());
          } catch (e) {
            /* ignore */
          }
          cart = this.getCart();
        }
        this.cart = cart;
        return cart;
      }

      async onSubmitHandler(evt) {
        const activeButton = evt.submitter || this.submitButton;
        const isCheckout = activeButton?.hasAttribute('data-checkout-button');

        if (isCheckout) {
          evt.preventDefault();
          if (activeButton.getAttribute('aria-disabled') === 'true' || activeButton.hasAttribute('disabled')) return;

          let returnToInput = this.form.querySelector('[name="return_to"]');
          if (!returnToInput) {
            returnToInput = document.createElement('input');
            returnToInput.type = 'hidden';
            returnToInput.name = 'return_to';
            this.form.appendChild(returnToInput);
          }
          returnToInput.value = '/checkout';

          activeButton.setAttribute('aria-disabled', true);
          activeButton.classList.add('loading');
          activeButton.querySelector('.loading__spinner')?.classList.remove('hidden');
          this.form.submit();
          return;
        }

        evt.preventDefault();
        evt.stopPropagation();
        if (activeButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        activeButton.setAttribute('aria-disabled', true);
        activeButton.classList.add('loading');
        activeButton.querySelector('.loading__spinner')?.classList.remove('hidden');

        const cart = await this.ensureCart();

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cartReady(cart)) {
          formData.append(
            'sections',
            cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement?.(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              if (typeof cart?.close === 'function') cart.close();

              const soldOutMessage = activeButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              activeButton.setAttribute('aria-disabled', true);
              activeButton.querySelector('span')?.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cartReady(cart)) {
              // 抽屉未挂载时才回退；有 cart-drawer 节点时不再跳旧购物车页
              if (!this.getCart()) {
                window.location = window.routes.cart_url;
              }
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            activeButton.classList.remove('loading');
            if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
            if (!this.error) activeButton.removeAttribute('aria-disabled');
            activeButton.querySelector('.loading__spinner')?.classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.checkoutButton?.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.checkoutButton?.removeAttribute('disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
